import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import SpeedIcon from '@mui/icons-material/Speed';
import { priceToString } from 'shared/utils';

import Prorate from 'features/tenant/Prorate';
import { formatBuildingUpgrade, formatTenantName } from 'shared/utils/formatters';
import Select from 'shared/components/select';

/**
 * @param {Object} props
 * @param {boolean} props.cancelSub -
 * @param {Object} props.currentBuilding
 * @param {boolean} props.forceSubscriptionChange
 * @param {boolean} props.movingSamePlan
 * @param {Object} props.newBuilding
 * @param {Array<Object>}props.newBuildingUpgrades
 * @param {Object} props.newPlan
 * @param {Function} props.onPlanChange
 * @param {Object} props.subscription
 * @param {boolean} props.subscriptionConfirm
 * @param {Object} props.tenant
 * @param {Function} props.toggleConfirm
 * @returns {React.JSX.Element}
 */
export default function InternetServiceChanger({
  cancelSub,
  currentBuilding,
  forceSubscriptionChange,
  movingSamePlan,
  newBuilding,
  newBuildingUpgrades,
  newPlan,
  onPlanChange,
  subscription,
  subscriptionConfirm,
  tenant,
  toggleConfirm,
}) {
  const [changeService, setChangeService] = useState('cancel');
  const [inputValue, setInputValue] = useState(undefined);
  const [disableConfirm, setDisableConfirm] = useState(false);
  const internetServiceOptions = newBuildingUpgrades.map((u) => (
    {
      id: u.plan.id,
      label: formatBuildingUpgrade(u),
    }
  ));

  let newQosDescription;
  let buildingPlan;
  if (newPlan) {
    const buildingUpgrade = newBuildingUpgrades.find((u) => (u.plan_id === newPlan.id));
    buildingPlan = buildingUpgrade.plan || {};
    newQosDescription = <strong>{`${buildingPlan.product.qos.description}`}</strong>;
  } else if (movingSamePlan) {
    newQosDescription = (
      <span>
        <strong>
          {`${subscription.plan.product.qos.description} at ${priceToString(subscription.plan.price)}`}
        </strong>
        , prorated with the billing cycle starting
        <strong> today</strong>
        , then recharging monthly until cancelled.
      </span>
    );
  } else if (newBuilding.qos.speed > 0) {
    newQosDescription = <strong>{`${newBuilding.qos.description} amenity service.`}</strong>;
  } else {
    newQosDescription = <strong>{`${newBuilding.qos.description || 'none'}.`}</strong>;
  }

  useEffect(() => {
    if (changeService === 'new-sub' && !newPlan?.id) {
      setDisableConfirm(true);
      return;
    }
    setDisableConfirm(false);
  }, [newPlan, changeService]);

  return (
    <Grid size={12}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <SpeedIcon color="primary" />
        <Typography variant="h6">Internet Service Change</Typography>
      </Stack>
      {forceSubscriptionChange && (
        <>
          <Typography variant="subtitle1">Select a new internet service:</Typography>
          <FormControl>
            <RadioGroup
              aria-labelledby="service-change-label"
              name="controlled-radio-buttons-group"
              value={changeService}
              onChange={(event, value) => {
                setChangeService(value);
              }}
            >
              <FormControlLabel
                value="cancel"
                control={<Radio />}
                label={'Set to building\'s default speed'}
              />
              <FormControlLabel
                value="new-sub"
                control={<Radio />}
                label="Select new subscription plan"
              />
              <Select
                label="Availabile Plans"
                selectedOption={newPlan}
                onChange={onPlanChange}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.id}
                inputValue={inputValue}
                setInputValue={setInputValue}
                options={internetServiceOptions}
                disabled={changeService !== 'new-sub'}
              />
            </RadioGroup>
          </FormControl>
          <Prorate
            currentPrice={subscription.plan.price}
            newPlanId={newPlan ? newPlan.id : ''}
            newPrice={newPlan ? buildingPlan.price : subscription.plan.price}
            tenantId={tenant.id}
          />
        </>
      )}
      <Typography
        sx={{ mb: 1 }}
        variant="body1"
      >
        {`${formatTenantName(tenant)}'s new internet service will be: `}
        {newQosDescription}
      </Typography>
      {(newBuilding.id !== currentBuilding.id && newPlan) && (
        <Typography sx={{ mb: 1 }} variant="body1">
          This will start a
          <strong>{' new monthly billing cycle anchored today '}</strong>
          because the building is changing.
        </Typography>
      )}
      {cancelSub && (
        <Typography
          variant="body1"
          sx={{
            fontWeight: 'bold',
            mb: 1,
          }}
        >
          Current
          {subscription.voucher ? ' voucher ' : ' subscription '}
          will be cancelled immediately.
        </Typography>
      )}
      <FormControl error={disableConfirm}>
        <FormControlLabel
          disabled={disableConfirm}
          control={(
            <Checkbox
              checked={subscriptionConfirm}
              onChange={toggleConfirm}
            />
          )}
          label="Confirm service change"
        />
        <FormHelperText>{disableConfirm && 'Must select a new plan for subscription change'}</FormHelperText>
      </FormControl>
      <hr />
    </Grid>
  );
}
